/**
 * Configuration — Permissions & Access matrix, sample Permission Configurations,
 * and assignment helpers (paired with Team Members).
 */

import {
  CONFIGURATION_STATUS,
  DEFAULT_DATA_ACCESS_SCOPE_OPTIONS,
  emptyModulePermission,
  getDefaultDataAccessScopeLabel,
  getModuleLabel,
  MODULE_ACTION_KEYS,
  MODULE_ACTION_LABELS,
  moduleHasAnyPermission,
  normalizeModulePermissions,
  PERMISSIONS_LIST_PREVIEW_COUNT,
} from './permissionsConfig';

export const TEAM_TYPE_OPTIONS = ['Support', 'Development', 'Finance', 'Design & Content'];

/** Team Type → Departments → Job Roles (Preset Matrix). */
export const PRESET_MATRIX = {
  Support: {
    departments: ['Support Team'],
    jobRolesByDepartment: {
      'Support Team': ['Support', 'Support Manager'],
    },
  },
  Development: {
    departments: ['Development Team'],
    jobRolesByDepartment: {
      'Development Team': ['Front-end Developer', 'Back-end Developer', 'Full-stack Developer'],
    },
  },
  Finance: {
    departments: ['Finance Team'],
    jobRolesByDepartment: {
      'Finance Team': ['Finance Manager', 'Accountant', 'Finance Officer'],
    },
  },
  'Design & Content': {
    departments: ['Content Team', 'Marketing Team'],
    jobRolesByDepartment: {
      'Content Team': ['Content Writer', 'Blog Editor'],
      'Marketing Team': ['Social Media Coordinator', 'Marketing Officer'],
    },
  },
};

/** Explicit Owner-selectable configuration exceptions (never automatic defaults). */
export const CONFIGURATION_EXCEPTION_MODULES = [
  'configuration_validation',
  'configuration_notifications',
  'configuration_limits',
  'configuration_flag',
];

/** Never assignable to ordinary Team Members. */
export const NON_ASSIGNABLE_MODULES = [
  'configuration_permissions_access',
  'configuration_retention',
];

export const CONFIGURATION_MODULES_FOR_MAINTENANCE = [
  'configuration',
  'configuration_validation',
  'configuration_notifications',
  'configuration_limits',
  'configuration_flag',
  'integrations',
  'form_inventory',
  'pricing_plans',
];

export const ACCESS_DURATION = {
  ONGOING: 'Ongoing',
  TEMPORARY: 'Temporary',
};

export const EXTENDED_MODULE_ACTION_KEYS = [
  'view',
  'add',
  'edit',
  'assign',
  'review',
  'changeStatus',
  'addNote',
  'generate',
  'download',
  'activate',
  'deactivate',
  'archive',
];

export const EXTENDED_MODULE_ACTION_LABELS = {
  view: 'View',
  add: 'Add',
  edit: 'Edit',
  assign: 'Assign',
  review: 'Review',
  changeStatus: 'Change Status',
  addNote: 'Add Note',
  generate: 'Generate',
  download: 'Download',
  activate: 'Activate',
  deactivate: 'Deactivate',
  archive: 'Archive',
};

function row(module, actions, dataAccessScope = 'department_records') {
  const base = emptyModulePermission(module);
  return {
    ...base,
    ...actions,
    view: actions.view !== false,
    dataAccessScope,
  };
}

function formatActions(moduleRow) {
  return EXTENDED_MODULE_ACTION_KEYS.filter((k) => moduleRow[k]).map(
    (k) => EXTENDED_MODULE_ACTION_LABELS[k] || MODULE_ACTION_LABELS[k] || k,
  );
}

export function getDepartmentsForTeamType(teamType) {
  return PRESET_MATRIX[teamType]?.departments || [];
}

export function getJobRolesForDepartment(teamType, department) {
  return PRESET_MATRIX[teamType]?.jobRolesByDepartment?.[department] || [];
}

export function findActivePermissionConfig(configs, { teamType, department, jobRole } = {}) {
  return (configs || []).find(
    (c) =>
      c.status === CONFIGURATION_STATUS.ACTIVE &&
      c.teamType === teamType &&
      c.department === department &&
      c.jobRole === jobRole,
  );
}

export function isConfigurationModule(moduleValue) {
  return (
    CONFIGURATION_MODULES_FOR_MAINTENANCE.includes(moduleValue) ||
    String(moduleValue || '').startsWith('configuration_')
  );
}

export function isModuleAssignableToOrdinaryMember(moduleValue) {
  return !NON_ASSIGNABLE_MODULES.includes(moduleValue);
}

export function getConfigModuleLabels(config) {
  return normalizeModulePermissions(config?.modulePermissions)
    .filter(moduleHasAnyPermission)
    .map((r) => getModuleLabel(r.module) || r.moduleLabel || r.module)
    .filter(Boolean);
}

export function getConfigModulePreviewRows(config) {
  return normalizeModulePermissions(config?.modulePermissions)
    .filter(moduleHasAnyPermission)
    .map((r) => ({
      id: r.module,
      name: getModuleLabel(r.module) || r.moduleLabel || r.module,
    }));
}

export function getConfigPermissionPreviewParts(config) {
  const rows = normalizeModulePermissions(config?.modulePermissions).filter(moduleHasAnyPermission);
  const previewRows = rows.map((r) => {
    const actions = formatActions(r).join(', ');
    const scope =
      getDefaultDataAccessScopeLabel(r.dataAccessScope) || getAccessScopeCompat(r.dataAccessScope);
    return {
      id: r.module,
      name: `${getModuleLabel(r.module)}: ${actions} — ${scope}`,
    };
  });
  const lines = previewRows.map((r) => r.name);
  const extraCount = Math.max(0, lines.length - PERMISSIONS_LIST_PREVIEW_COUNT);
  const previewLines = lines.slice(0, PERMISSIONS_LIST_PREVIEW_COUNT);
  return {
    lines,
    previewRows,
    previewLines,
    extraCount,
    previewText: previewLines.length
      ? `${previewLines.join(' · ')}${extraCount > 0 ? ` · +${extraCount} more` : ''}`
      : '—',
  };
}

function getAccessScopeCompat(value) {
  const map = {
    assigned_records: 'Assigned Records Only',
    team_queue: 'Department Records',
    related_records: 'Department Records',
    all_area_records: 'All Module Records',
    assigned_records_only: 'Assigned Records Only',
    department_records: 'Department Records',
    all_module_records: 'All Module Records',
    not_applicable: 'Not Applicable',
  };
  return (
    map[value] ||
    DEFAULT_DATA_ACCESS_SCOPE_OPTIONS.find((o) => o.value === value)?.label ||
    value ||
    '—'
  );
}

export function formatConfigModuleColumnParts(config) {
  const labels = getConfigModuleLabels(config);
  const previewRows = getConfigModulePreviewRows(config);
  const extraCount = Math.max(0, labels.length - PERMISSIONS_LIST_PREVIEW_COUNT);
  const previewNames = labels.slice(0, PERMISSIONS_LIST_PREVIEW_COUNT);
  return {
    labels,
    previewRows,
    previewNames,
    extraCount,
    previewText: previewNames.length
      ? `${previewNames.join(' · ')}${extraCount > 0 ? ` · +${extraCount} more` : ''}`
      : '—',
  };
}

/** Illustrative Permission Configurations for Screen 1. */
export function buildSamplePermissionConfigs() {
  return [
    {
      id: 'PERM-0001',
      permissionPresetName: 'Support Preset',
      teamType: 'Support',
      department: 'Support Team',
      jobRole: 'Support',
      defaultAccessLabel: 'Support Access',
      dashboard: 'Support Team Dashboard',
      customisableModules: ['support_tickets', 'appeals', 'customers', 'businesses'],
      modulePermissions: [
        row('support_tickets', { view: true, assign: true, review: true }, 'department_records'),
        row('appeals', { view: true, review: true }, 'department_records'),
        row('customers', { view: true }, 'assigned_records_only'),
      ],
      status: CONFIGURATION_STATUS.ACTIVE,
      dateAdded: '2026-05-12T00:00:00.000Z',
      lastUpdated: '2026-07-28T14:35:00.000Z',
      lockedIdentity: false,
    },
    {
      id: 'PERM-0002',
      permissionPresetName: 'Support Manager Preset',
      teamType: 'Support',
      department: 'Support Team',
      jobRole: 'Support Manager',
      defaultAccessLabel: 'Support Manager Access',
      dashboard: 'Support Team Dashboard',
      customisableModules: ['moderation', 'reports', 'support_tickets', 'appeals'],
      modulePermissions: [
        row('moderation', { view: true }, 'department_records'),
        row('reports', { view: true, generate: true }, 'department_records'),
        row('support_tickets', { view: true, assign: true, review: true, edit: true }, 'department_records'),
      ],
      status: CONFIGURATION_STATUS.ACTIVE,
      dateAdded: '2026-05-14T00:00:00.000Z',
      lastUpdated: '2026-07-27T16:20:00.000Z',
      lockedIdentity: false,
    },
    {
      id: 'PERM-0003',
      permissionPresetName: 'Front-end Developer Preset',
      teamType: 'Development',
      department: 'Development Team',
      jobRole: 'Front-end Developer',
      defaultAccessLabel: 'Front-end Developer Access',
      dashboard: 'Development Team Dashboard',
      customisableModules: ['posts_management', 'keywords_management', 'content'],
      modulePermissions: [
        row('posts_management', { view: true, add: true, edit: true }, 'all_module_records'),
        row('keywords_management', { view: true, add: true, edit: true }, 'all_module_records'),
        row('content', { view: true, edit: true }, 'all_module_records'),
      ],
      status: CONFIGURATION_STATUS.ACTIVE,
      dateAdded: '2026-05-18T00:00:00.000Z',
      lastUpdated: '2026-07-26T11:15:00.000Z',
      lockedIdentity: false,
    },
    {
      id: 'PERM-0004',
      permissionPresetName: 'Finance Manager Preset',
      teamType: 'Finance',
      department: 'Finance Team',
      jobRole: 'Finance Manager',
      defaultAccessLabel: 'Finance Manager Access',
      dashboard: 'Finance Team Dashboard',
      customisableModules: ['billing_subscriptions', 'reports', 'pricing_plans'],
      modulePermissions: [
        row('billing_subscriptions', { view: true }, 'all_module_records'),
        row('reports', { view: true, generate: true, download: true }, 'all_module_records'),
      ],
      status: CONFIGURATION_STATUS.INACTIVE,
      dateAdded: '2026-05-20T00:00:00.000Z',
      lastUpdated: '2026-07-25T15:10:00.000Z',
      lockedIdentity: false,
    },
    {
      id: 'PERM-0005',
      permissionPresetName: 'Back-end Developer Preset',
      teamType: 'Development',
      department: 'Development Team',
      jobRole: 'Back-end Developer',
      defaultAccessLabel: 'Back-end Developer Access',
      dashboard: 'Development Team Dashboard',
      customisableModules: ['trade_categories_management', 'integrations', 'configuration_validation'],
      modulePermissions: [
        row('trade_categories_management', { view: true, edit: true }, 'all_module_records'),
        row('integrations', { view: true }, 'not_applicable'),
        row('configuration_validation', { view: true, edit: true }, 'not_applicable'),
      ],
      status: CONFIGURATION_STATUS.ACTIVE,
      dateAdded: '2026-05-22T00:00:00.000Z',
      lastUpdated: '2026-07-24T10:30:00.000Z',
      lockedIdentity: false,
    },
  ];
}

export function emptyConfigurationMaintenance(moduleValue) {
  return {
    module: moduleValue,
    duration: ACCESS_DURATION.ONGOING,
    expiry: '',
  };
}

export function buildMaintenanceBlocks(modulePermissions, existing = []) {
  const existingMap = new Map((existing || []).map((b) => [b.module, b]));
  return normalizeModulePermissions(modulePermissions)
    .filter(moduleHasAnyPermission)
    .filter((r) => isConfigurationModule(r.module))
    .map((r) => {
      const prev = existingMap.get(r.module);
      return {
        module: r.module,
        assignedActions: formatActions(r),
        duration: prev?.duration || ACCESS_DURATION.ONGOING,
        expiry: prev?.expiry || '',
      };
    });
}

export function collectPermissionConfigSaveErrors(form, { configs = [], excludeId = null } = {}) {
  const errors = {};
  if (!(form.permissionPresetName || '').trim()) {
    errors.permissionPresetName = 'Enter Permission Preset name.';
  }
  if (!form.teamType) errors.teamType = 'Select Team Type.';
  if (!form.department) errors.department = 'Select Department.';
  if (!form.jobRole) errors.jobRole = 'Select Job Role.';
  if (!form.status) errors.status = 'Select Configuration Status.';

  const granted = normalizeModulePermissions(form.modulePermissions).filter(moduleHasAnyPermission);
  if (!granted.length) errors.modulePermissions = 'Select at least one module with View.';

  granted.forEach((r) => {
    if (!r.view) {
      errors.modulePermissions = 'Every selected module must include View.';
    }
  });

  const duplicate = (configs || []).some(
    (c) =>
      c.id !== excludeId &&
      c.teamType === form.teamType &&
      c.department === form.department &&
      c.jobRole === form.jobRole,
  );
  if (duplicate) {
    errors.jobRole = 'This Team Type–Department–Job Role combination already exists.';
  }

  return errors;
}

export function applyPermissionConfigSave(existing, form, { actorName = 'Platform Owner' } = {}) {
  const now = new Date().toISOString();
  return {
    ...existing,
    permissionPresetName: form.permissionPresetName.trim(),
    teamType: form.teamType,
    department: form.department,
    jobRole: form.jobRole,
    defaultAccessLabel: form.defaultAccessLabel || existing.defaultAccessLabel,
    dashboard: form.dashboard || existing.dashboard,
    customisableModules: form.customisableModules || existing.customisableModules || [],
    modulePermissions: normalizeModulePermissions(form.modulePermissions),
    status: form.status,
    lastUpdated: now,
    lastUpdatedBy: actorName,
  };
}

/** Eligible ordinary members for Add Access dropdown. */
export function getAssignableTeamMembers(members) {
  return (members || []).filter(
    (m) =>
      m &&
      !m.isOwner &&
      !m.isCoOwner &&
      m.platformRole !== 'Platform Owner' &&
      m.platformRole !== 'Owner' &&
      m.platformRole !== 'Co-owner' &&
      ['Pending Access', 'Active', 'Inactive'].includes(m.status),
  );
}

export function formatMemberDropdownLabel(member, allAssignable) {
  if (!member) return '';
  const name = member.name || '—';
  const duplicates = (allAssignable || []).filter(
    (m) => (m.name || '').toLowerCase() === name.toLowerCase(),
  );
  if (duplicates.length > 1) {
    return `${name} (${member.workEmail || '—'})`;
  }
  return name;
}

// Re-export action helpers used by forms that need extended keys
export function moduleHasExtendedPermission(row) {
  if (!row) return false;
  return EXTENDED_MODULE_ACTION_KEYS.some((key) => Boolean(row[key]));
}

export function formatExtendedModuleActionsSummary(row) {
  if (!row || !moduleHasExtendedPermission(row)) return 'No access';
  return formatActions(row).join(', ') || 'No access';
}

export {
  MODULE_ACTION_KEYS,
  MODULE_ACTION_LABELS,
  getAccessScopeCompat as getSpecAccessScopeLabel,
};
